import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@carrier-io/air-react";
import Typography from "@carrier-io/air-react/Typography";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import CheckIcon from "@mui/icons-material/Check";
import { useTheme } from "@carrier-io/air-react/styles";
import Box from "@carrier-io/air-react/Box";
import { TableEditIcon } from "./Icons/TableEditIcon";
import IconButton from "@carrier-io/air-react/IconButton";
import { UserStatus } from "../../generated/ups/graphql";
import { RegionsType } from "../../constants/regions";
import { RoutesPage } from "../../constants/routes";
import { StatusTag } from "../statusTag/StatusTag";
import { formatDateToUI } from "../../utils/dates";

export type UserDataDisplayType = {
  username: string;
  lastName: string;
  firstName: string;
  email: string;
  groups: string;
  roles: string;
  lastLogin?: string;
  attributes?: Record<string, unknown>;
  status: UserStatus;
};

type UserManagementTableProps = {
  users: UserDataDisplayType[];
  region: RegionsType;
  isLoading?: boolean;
  isActivePagination?: boolean;
  onNext: () => void;
  onPrev: () => void;
  isNextDisabled?: boolean;
  isPrevDisabled?: boolean;
};

const DEFAULT_DISPLAYED_USERS = Array.from({ length: 10 }, () => null);

export const UserManagementTable = ({
  users,
  region,
  isLoading,
  isActivePagination,
  onNext,
  isNextDisabled,
  onPrev,
  isPrevDisabled,
}: UserManagementTableProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleEditUser = (currentUser: UserDataDisplayType) => {
    const searchParams = new URLSearchParams({
      username: currentUser.username,
      region: region,
    });
    navigate({
      pathname: RoutesPage.EditUser,
      search: searchParams.toString(),
    });
  };
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              align="left"
              sx={{
                minWidth: "110px",
                width: "8.75%",
                paddingLeft: 1,
                paddingRight: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography variant="body2Bold">Last Name</Typography>
              </Box>
            </TableCell>
            <TableCell
              align="left"
              sx={{
                minWidth: "110px",
                width: "8.75%",
                paddingLeft: 1,
                paddingRight: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography variant="body2Bold">First Name</Typography>
              </Box>
            </TableCell>
            <TableCell
              align="left"
              sx={{
                minWidth: "180px",
                width: "15%",
                paddingLeft: 1,
                paddingRight: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography variant="body2Bold">Email Address</Typography>
              </Box>
            </TableCell>
            <TableCell
              align="left"
              sx={{
                minWidth: "140px",
                width: "15%",
                paddingLeft: 1,
                paddingRight: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography variant="body2Bold">Groups</Typography>
              </Box>
            </TableCell>
            <TableCell
              align="left"
              sx={{
                minWidth: "140px",
                width: "20%",
                paddingLeft: 1,
                paddingRight: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography variant="body2Bold">Roles</Typography>
              </Box>
            </TableCell>
            <TableCell
              align="left"
              sx={{
                minWidth: "100px",
                width: "10%",
                paddingLeft: 1,
                paddingRight: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography variant="body2Bold">Custom Attributes</Typography>
              </Box>
            </TableCell>
            <TableCell
              align="left"
              sx={{
                minWidth: "90px",
                width: "7.5%",
                paddingLeft: 1,
                paddingRight: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography variant="body2Bold">Status</Typography>
              </Box>
            </TableCell>{" "}
            <TableCell
              align="left"
              sx={{
                minWidth: "105px",
                width: "15%",
                paddingLeft: 1,
                paddingRight: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography variant="body2Bold">Last Login</Typography>
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ paddingLeft: 1, paddingRight: 1 }}>
              <Box sx={{ width: "46px" }}></Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading
            ? DEFAULT_DISPLAYED_USERS.map((_, index) => (
                <TableRow key={`skeleton-${index}`} sx={{ height: "65px" }}>
                  <TableCell colSpan={9}>
                    <Skeleton
                      data-testid="skeleton"
                      variant="rectangular"
                      width="100%"
                      height="6px"
                      sx={{ borderRadius: "8px" }}
                    />
                  </TableCell>
                </TableRow>
              ))
            : users.map((user, idx) => (
                <TableRow key={`user-${user.username + idx}`}>
                  <TableCell>{user.lastName}</TableCell>
                  <TableCell>{user.firstName}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.groups}</TableCell>
                  <TableCell>{user.roles}</TableCell>
                  <TableCell>
                    {user.attributes &&
                    Object.values(user.attributes).length ? (
                      <Tooltip
                        arrow
                        title={JSON.stringify(user.attributes, null, 2)}
                      >
                        <CheckIcon
                          fontSize="small"
                          sx={{
                            color: theme.palette.success.main,
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell>
                    {user.status && <StatusTag status={user.status} />}
                  </TableCell>
                  <TableCell>
                    {user.lastLogin ? formatDateToUI(user.lastLogin) : ""}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ width: "46px" }}>
                      <IconButton
                        data-testid="edit-user-button"
                        onClick={() => handleEditUser(user)}
                      >
                        <TableEditIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>
      {isActivePagination && (
        <Box
          justifyContent="flex-end"
          display="flex"
          width="100%"
          height="32px"
          padding="12px 0"
        >
          <IconButton disabled={isPrevDisabled}>
            <NavigateBeforeIcon
              onClick={onPrev}
              fontSize="small"
              sx={{
                color: isPrevDisabled
                  ? theme.palette.text.disabled
                  : theme.palette.text.secondary,
              }}
            />
          </IconButton>
          <IconButton disabled={isNextDisabled} sx={{ marginRight: "50px" }}>
            <NavigateNextIcon
              data-testid="next-page-button"
              onClick={onNext}
              fontSize="small"
              sx={{
                color: isNextDisabled
                  ? theme.palette.text.disabled
                  : theme.palette.text.secondary,
              }}
            />
          </IconButton>
        </Box>
      )}
    </TableContainer>
  );
};
